import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { SkeletonTable } from '../../components/common/Skeleton';
import CopyButton from '../../components/common/CopyButton';
import { FileText, Search, Download, Filter, RefreshCw } from 'lucide-react';

const ReportsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/admin/audit-logs?limit=200&type=${typeFilter}`);
      setTransactions(res.data.transactions || []);
    } catch (err) {
      console.error('[Reports Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [typeFilter]);

  const filteredLogs = transactions.filter(tx => {
    const term = searchTerm.toLowerCase();
    return (
      tx.userId?.name?.toLowerCase().includes(term) ||
      tx.userId?.email?.toLowerCase().includes(term) ||
      tx.userId?.userIdTag?.toLowerCase().includes(term) ||
      tx.notes?.toLowerCase().includes(term) ||
      tx.type?.toLowerCase().includes(term)
    );
  });

  const handleExportCSV = () => {
    if (filteredLogs.length === 0) return;
    
    const headers = ['Transaction ID', 'User ID Tag', 'User Name', 'User Email', 'Type', 'Amount', 'Balance Before', 'Balance After', 'Notes', 'Date'];
    const rows = filteredLogs.map(tx => [
      tx._id,
      tx.userId?.userIdTag || '',
      `"${tx.userId?.name || ''}"`,
      tx.userId?.email || '',
      tx.type,
      tx.amount,
      tx.balanceBefore,
      tx.balanceAfter,
      `"${(tx.notes || '').replace(/"/g, '""')}"`,
      new Date(tx.createdAt).toISOString()
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `financial_audit_ledger_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-[#F97316]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">Platform Financial Audit Reports</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Search, filter, and export the complete immutable transaction ledger logs across all users, deposits, profit deductions, and withdrawals.
          </p>
        </div>

        <div className="flex items-center space-x-3 shrink-0">
          <Button variant="secondary" size="sm" onClick={fetchAuditLogs} className="flex items-center space-x-2">
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Refresh</span>
          </Button>

          <Button variant="primary" size="sm" onClick={handleExportCSV} className="flex items-center space-x-2">
            <Download className="w-3.5 h-3.5" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#121215] p-4 rounded-2xl border border-[#27272A]">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search User ID, Name, Email, Notes..."
            className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#F97316]"
          />
        </div>

        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <Filter className="w-4 h-4 text-[#9CA3AF]" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-[#09090B] border border-[#27272A] text-[#FFFFFF] text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#F97316] w-full sm:w-auto"
          >
            <option value="all">All Transaction Types</option>
            <option value="deposit_approval">Deposit Approvals</option>
            <option value="profit_deduction">Profit Deductions</option>
            <option value="referral_commission">Referral Commissions</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="withdrawal_reversal">Withdrawal Reversals</option>
          </select>
        </div>
      </div>

      {/* Full Audit Ledger Table */}
      <Card title={`Immutable Financial Audit Ledger (${filteredLogs.length})`} subtitle="Full ledger with balance before & after snapshots">
        {loading ? (
          <SkeletonTable rows={6} cols={7} />
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No audit logs match your search filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Bal Before</th>
                  <th className="py-3 px-4">Bal After</th>
                  <th className="py-3 px-4">Description / Notes</th>
                  <th className="py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {filteredLogs.map((tx) => (
                  <tr key={tx._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5">
                        <span className="font-bold text-[#FFFFFF]">{tx.userId?.name || 'System'}</span>
                        {tx.userId?.userIdTag && (
                          <span className="inline-flex items-center space-x-1 font-mono text-[10px] text-[#F59E0B]">
                            <span>({tx.userId?.userIdTag})</span>
                            <CopyButton text={tx.userId?.userIdTag} />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                        <span>{tx.userId?.email}</span>
                        {tx.userId?.email && <CopyButton text={tx.userId?.email} />}
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase ${
                        tx.type === 'deposit_approval' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        tx.type === 'referral_commission' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        tx.type === 'profit_deduction' ? 'bg-[#F97316]/20 text-[#F97316]' :
                        tx.type === 'withdrawal' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                        'bg-[#27272A] text-[#9CA3AF]'
                      }`}>
                        {tx.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className={`py-3.5 px-4 font-bold ${
                      tx.type === 'withdrawal' || tx.type === 'profit_deduction' 
                        ? 'text-[#EF4444]' 
                        : 'text-[#10B981]'
                    }`}>
                      {tx.amount > 0 ? `+$${tx.amount.toFixed(2)}` : `-$${Math.abs(tx.amount).toFixed(2)}`}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">${tx.balanceBefore?.toFixed(2) || '0.00'}</td>
                    <td className="py-3.5 px-4 font-mono font-bold text-[#FFFFFF]">${tx.balanceAfter?.toFixed(2) || '0.00'}</td>
                    <td className="py-3.5 px-4 text-[#9CA3AF] max-w-sm break-all">{tx.notes || tx.description || '-'}</td>
                    <td className="py-3.5 px-4 text-[#9CA3AF] text-[11px] whitespace-nowrap">
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportsPage;
